import { Pair } from '../../../types/pair';

interface FilterValue {
  from: string;
  to: string;
  enabled: boolean;
}

interface MotionFilters {
  motionPercent: FilterValue;
  bmotionPercent: FilterValue;
  maxMCPercent: FilterValue;
  openPrice: FilterValue;
  totalPairFees: FilterValue;
  migrationPercent: FilterValue;
  motionGreaterBMotion: FilterValue;
}

/**
 * Checks if a filter is actually enabled (at least one value is set)
 * For switch-type filters, checks the enabled flag
 */
export const isFilterActuallyEnabled = (filter: FilterValue, isSwitchType: boolean = false): boolean => {
  // For switch-type filters, check the enabled flag
  if (isSwitchType) {
    return filter.enabled;
  }
  // For range/min filters, check if any value is set
  const fromSet = filter.from.trim() !== '';
  const toSet = filter.to.trim() !== '';
  return fromSet || toSet;
};

/**
 * Checks if a value is within a range defined by from/to strings
 */
export const isValueInRange = (value: number | null | undefined, from: string, to: string): boolean => {
  if (value === null || value === undefined) return false;

  // Convert value to number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return false;

  const fromNum = from.trim() === '' ? -Infinity : parseFloat(from);
  const toNum = to.trim() === '' ? Infinity : parseFloat(to);

  if (isNaN(fromNum) || isNaN(toNum)) return true; // If range is invalid, don't filter

  return numValue >= fromNum && numValue <= toNum;
};

/**
 * Calculates migration percentage from migrationCount and devPairCount
 * @param migrationCount - Number of migrated pairs
 * @param devPairCount - Total number of dev pairs
 * @returns Migration percentage (0-100) or null if calculation is not possible
 */
export const calculateMigrationPercent = (migrationCount?: number, devPairCount?: number): number | null => {
  if (migrationCount !== null &&
    migrationCount !== undefined &&
    devPairCount &&
    devPairCount > 0) {
    return (migrationCount / devPairCount) * 100;
  }
  return null;
};

/**
 * Calculates open value from openPrice and supply
 */
const calculateOpenValue = (openPrice?: number, supply?: number): number | null => {
  if (openPrice === undefined || openPrice === null) return null;
  return openPrice * (supply || 1);
};

/**
 * Calculates average percent value from an array of related pairs
 * @param relatedPairs - Array of related pairs
 * @param getPercentValue - Function to extract percent value from a pair
 * @returns Average percent value or null if no valid values found
 */
export const calculateAveragePercent = (
  relatedPairs: Pair[] | undefined,
  getPercentValue: (pair: Pair) => number | null | undefined
): number | null => {
  if (!Array.isArray(relatedPairs) || relatedPairs.length === 0) {
    return null;
  }

  const validValues = relatedPairs
    .map(getPercentValue)
    .filter((value): value is number => value !== null && value !== undefined && !isNaN(value));

  if (validValues.length === 0) {
    return null;
  }

  const sum = validValues.reduce((acc, val) => acc + val, 0);
  return sum / validValues.length;
};

/**
 * Checks if a related pair matches Motion/BMotion/MaxMC/OpenPrice/MigrationPercent filters
 * @param relatedPair - The related pair to check
 * @param filters - The filter settings
 * @returns true if the pair matches all enabled filters, false otherwise
 */
export const doesRelatedPairMatchFilters = (
  relatedPair: Pair,
  filters: MotionFilters
): boolean => {
  const motionPercentFilterEnabled = isFilterActuallyEnabled(filters.motionPercent);
  const bmotionPercentFilterEnabled = isFilterActuallyEnabled(filters.bmotionPercent);
  const maxMCPercentFilterEnabled = isFilterActuallyEnabled(filters.maxMCPercent);
  const openPriceFilterEnabled = isFilterActuallyEnabled(filters.openPrice);
  const totalPairFeesFilterEnabled = isFilterActuallyEnabled(filters.totalPairFees);
  const motionGreaterBMotionEnabled = filters.motionGreaterBMotion.enabled;
  // Note: migrationPercent is checked on main pair, not related pairs

  // If no filters are enabled, return false (don't highlight)
  if (!motionPercentFilterEnabled && !bmotionPercentFilterEnabled && !maxMCPercentFilterEnabled &&
    !openPriceFilterEnabled && !totalPairFeesFilterEnabled && !motionGreaterBMotionEnabled) return false;

  let motionPercentMatches = true;
  let bmotionPercentMatches = true;
  let maxMCPercentMatches = true;
  let openPriceMatches = true;
  let totalPairFeesMatches = true;
  let motionGreaterBMotionMatches = true;

  if (motionPercentFilterEnabled) {
    motionPercentMatches = isValueInRange(
      relatedPair.motionPercent,
      filters.motionPercent.from,
      filters.motionPercent.to || ""
    );
  }

  if (bmotionPercentFilterEnabled) {
    bmotionPercentMatches = isValueInRange(
      relatedPair.bmotionPercent,
      filters.bmotionPercent.from,
      filters.bmotionPercent.to || ""
    );
  }

  if (maxMCPercentFilterEnabled) {
    maxMCPercentMatches = isValueInRange(
      relatedPair.maxMCPercent,
      filters.maxMCPercent.from,
      filters.maxMCPercent.to || ""
    );
  }

  if (openPriceFilterEnabled) {
    const openValue = calculateOpenValue(relatedPair.openPrice, relatedPair.supply);
    openPriceMatches = isValueInRange(
      openValue,
      filters.openPrice.from,
      filters.openPrice.to
    );
  }

  if (totalPairFeesFilterEnabled) {
    totalPairFeesMatches = isValueInRange(
      relatedPair.totalPairFeesPaid,
      filters.totalPairFees.from,
      filters.totalPairFees.to || ""
    );
  }

  if (motionGreaterBMotionEnabled) {
    // Check if BMotion > Motion
    // Both values must be valid numbers
    const bmotion = relatedPair.bmotion;
    const motion = relatedPair.motion;
    motionGreaterBMotionMatches =
      bmotion !== null && bmotion !== undefined &&
      motion !== null && motion !== undefined &&
      !isNaN(bmotion) && !isNaN(motion) &&
      bmotion > motion;
  }

  // A pair matches if all enabled filters match
  // Note: migrationPercent is checked on main pair, not here
  return motionPercentMatches && bmotionPercentMatches && maxMCPercentMatches && openPriceMatches && totalPairFeesMatches && motionGreaterBMotionMatches;
};

/**
 * Counts how many related pairs match Motion/BMotion/MaxMC/OpenPrice/MigrationPercent filters
 * @param relatedPairs - Array of related pairs to check
 * @param filters - The filter settings
 * @param howManyRelatedShouldMatch - Minimum number of related pairs that must match (default: 1)
 * @returns true if at least howManyRelatedShouldMatch pairs match all enabled filters, false otherwise
 */
export type RelatedMatchingMode = 'any' | 'latest';

export const hasRelatedPairMatchingFilters = (
  relatedPairs: Pair[] | undefined,
  filters: MotionFilters,
  howManyRelatedShouldMatch: number = 1,
  matchingMode: RelatedMatchingMode = 'latest'
): boolean => {
  if (!Array.isArray(relatedPairs) || relatedPairs.length === 0) return false;

  const motionPercentFilterEnabled = isFilterActuallyEnabled(filters.motionPercent);
  const bmotionPercentFilterEnabled = isFilterActuallyEnabled(filters.bmotionPercent);
  const maxMCPercentFilterEnabled = isFilterActuallyEnabled(filters.maxMCPercent);
  const openPriceFilterEnabled = isFilterActuallyEnabled(filters.openPrice);
  const totalPairFeesFilterEnabled = isFilterActuallyEnabled(filters.totalPairFees);
  const motionGreaterBMotionEnabled = filters.motionGreaterBMotion.enabled;
  // Note: migrationPercent is checked on main pair, not related pairs

  // If no filters are enabled, return true (don't filter)
  if (!motionPercentFilterEnabled && !bmotionPercentFilterEnabled && !maxMCPercentFilterEnabled &&
    !openPriceFilterEnabled && !totalPairFeesFilterEnabled && !motionGreaterBMotionEnabled) return true;

  const normalizedHowMany = Math.max(1, howManyRelatedShouldMatch);
  const normalizedMode: RelatedMatchingMode = matchingMode === 'latest' ? 'latest' : 'any';

  if (normalizedMode === 'latest') {
    if (relatedPairs.length < normalizedHowMany) {
      return false;
    }

    // Require the latest N related pairs (assumed to be pre-sorted by recency) to pass filters consecutively
    for (let index = 0; index < normalizedHowMany; index++) {
      if (!doesRelatedPairMatchFilters(relatedPairs[index], filters)) {
        return false;
      }
    }
    return true;
  }

  // Count how many related pairs match the enabled filters
  const matchingCount = relatedPairs.filter(relatedPair =>
    doesRelatedPairMatchFilters(relatedPair, filters)
  ).length;

  // Return true if count is at least the required minimum
  return matchingCount >= normalizedHowMany;
};
